import { GetTechnicalsConfigResponse } from '../../../proto/generated/overview-widget'
import { getTechnicalsConfig as fetchTechnicalsConfigApi } from '../../../services/apis/securities'
import { CONFIG_CACHE_TTL_MS, CONFIG_STORAGE_KEY } from './types'

type CachedConfig = {
    fetchedAt: number
    config: GetTechnicalsConfigResponse
}

let memoryCache: CachedConfig | null = null
let inFlight: Promise<GetTechnicalsConfigResponse> | null = null

const readStoredConfig = (): CachedConfig | null => {
    try {
        const raw = localStorage.getItem(CONFIG_STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as { fetchedAt: number; config: unknown }
        return {
            fetchedAt: parsed.fetchedAt,
            config: GetTechnicalsConfigResponse.fromJSON(parsed.config)
        }
    } catch {
        return null
    }
}

const writeStoredConfig = (cached: CachedConfig) => {
    try {
        localStorage.setItem(
            CONFIG_STORAGE_KEY,
            JSON.stringify({
                fetchedAt: cached.fetchedAt,
                config: GetTechnicalsConfigResponse.toJSON(cached.config)
            })
        )
    } catch {
        // ignore quota / private mode
    }
}

const isFresh = (fetchedAt: number) => Date.now() - fetchedAt < CONFIG_CACHE_TTL_MS

export const getTechnicalsConfigCached = async (forceRefresh = false): Promise<GetTechnicalsConfigResponse> => {
    if (!forceRefresh) {
        if (memoryCache && isFresh(memoryCache.fetchedAt)) {
            return memoryCache.config
        }
        const stored = readStoredConfig()
        if (stored && isFresh(stored.fetchedAt)) {
            memoryCache = stored
            return stored.config
        }
    }

    if (inFlight) return inFlight

    inFlight = fetchTechnicalsConfigApi()
        .then((config) => {
            const cached = { fetchedAt: Date.now(), config }
            memoryCache = cached
            writeStoredConfig(cached)
            return config
        })
        .finally(() => {
            inFlight = null
        })

    return inFlight
}
